import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Warning, Trash, ShieldCheck, Storefront, ArrowRight, SignIn } from "@phosphor-icons/react";
import NotificationPrefsCard from "../components/NotificationPrefsCard";
import ManageBillingButton from "../components/ManageBillingButton";
import EmailPrefsCard from "../components/EmailPrefsCard";
import CalendarSyncCard from "../components/CalendarSyncCard";
import GetTheAppCard from "../components/GetTheAppCard";
import { APP_VERSION, BUILD_STAMP } from "../lib/buildInfo";

/**
 * Settings — minimal account-management page shared by owners + customers.
 * For now contains one section: account deletion. Future home for password
 * change UI (already API-backed at /auth/change-password), notification
 * preferences, etc.
 */
export default function Settings() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  // While the auth context is still resolving the /auth/me request, show a
  // neutral skeleton instead of flashing the "must be signed in" state
  // (which used to confuse users into thinking the page itself was broken).
  if (authLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="settings-loading">
        <div className="h-7 w-32 bg-muted rounded animate-pulse" />
        <div className="mt-6 h-32 bg-muted/60 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!user) {
    // Real bug report (May 2026): an owner landed here while their session
    // had expired and saw a dead-end "must be signed in" message — they
    // could not figure out how to log in or where to edit their truck
    // profile. Give them a clear sign-in CTA + signup link, with ?next
    // carrying them back to /settings post-login.
    return (
      <div
        className="max-w-md mx-auto px-4 py-12 text-center"
        data-testid="settings-signin-required"
      >
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary grid place-items-center">
          <SignIn weight="duotone" size={24} />
        </div>
        <h1 className="font-display font-bold text-2xl tracking-tight mt-4">
          Sign in to manage your account
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Log in to change your password, edit notification preferences,
          or update your truck profile.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button
            type="button"
            onClick={() => navigate("/login?next=/settings")}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="settings-signin-btn"
          >
            Sign in
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/signup?next=/settings")}
            data-testid="settings-signup-btn"
          >
            Create an account
          </Button>
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          Looking to edit your truck profile or upload a logo? After signing
          in, head to the{" "}
          <Link to="/dashboard" className="text-primary underline-offset-2 hover:underline">
            owner dashboard
          </Link>
          .
        </p>
      </div>
    );
  }

  const isAdmin = user.is_admin;
  const isOwner = user.role === "owner";

  const onDelete = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.delete("/auth/me", {
        data: { password, confirm: "DELETE" },
      });
      toast.success("Account deleted. We're sorry to see you go.");
      // Clear in-memory auth state and bounce to landing.
      try { await logout(); } catch { /* server already cleared cookie */ }
      navigate("/", { replace: true });
      // Soft details to console for the user's own peace-of-mind in case they
      // want to verify what was removed.
      // eslint-disable-next-line no-console
      console.info("Deletion summary:", data);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't delete account");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="settings-page">
      <header>
        <h1 className="font-display font-bold text-2xl tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Signed in as <span className="font-semibold">{user.email}</span>
          {isOwner && " · Owner"}
          {!isOwner && !isAdmin && " · Customer"}
          {isAdmin && " · Admin"}
        </p>
      </header>

      {/* Account section */}
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="settings-account">
        <h2 className="font-display font-bold text-base mb-3">Account</h2>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/change-password")}
            data-testid="settings-change-password"
            className="gap-2"
          >
            Change password
          </Button>
          {isOwner && (user.is_pro || user.subscription_status) && (
            /* Stripe customer portal — also shown to past_due owners
               (payment failed drops is_pro, but they NEED this button) */
            <ManageBillingButton />
          )}
          {isOwner && user.is_pro && (
            <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    await api.post("/owner/pro-tour/reset", {});
                    toast.success("Pro tour re-armed. Open your dashboard to see it.");
                  } catch (e) {
                    toast.error(formatApiError(e) || "Couldn't reset the tour");
                  }
                }}
              data-testid="settings-replay-pro-tour"
              className="gap-2"
            >
              Replay Pro welcome tour
            </Button>
          )}
        </div>
      </section>

      {/* Truck profile entry — owners only. Real bug report 2026-05: owners
          went to /settings expecting to find profile / logo editing here.
          Wire it explicitly so the discovery path matches the mental
          model. */}
      {isOwner && (
        <Link
          to="/dashboard"
          className="block rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:bg-primary/[0.02] transition"
          data-testid="settings-truck-profile-entry"
        >
          <div className="flex items-start gap-3">
            <div className="flex-none h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Storefront weight="duotone" size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Truck profile
              </div>
              <h3 className="font-display font-bold text-base leading-snug mt-0.5">
                Edit your truck, logo, menu & weekly stops
              </h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Your truck name, weekly schedule, menu photos, and the public
                profile editor (logo, cover, links) all live on the owner
                dashboard.
              </p>
            </div>
            <ArrowRight
              size={20}
              weight="bold"
              className="flex-none text-muted-foreground"
            />
          </div>
        </Link>
      )}

      {/* Notifications */}
      <NotificationPrefsCard />

      {/* Email preferences — per-stream unsubscribe toggles */}
      <EmailPrefsCard />

      {/* Calendar sync — opt-in feed of followed-truck events */}
      <CalendarSyncCard />

      {/* Get-the-app — both stores are live. Hidden inside the native app. */}
      <GetTheAppCard compact />

      {/* Danger zone */}
      <section
        className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5"
        data-testid="settings-danger-zone"
      >
        <div className="flex items-start gap-3">
          <Warning weight="duotone" className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display font-bold text-base">Delete account</h2>
            {isAdmin ? (
              <p className="text-sm text-muted-foreground mt-1">
                Admin accounts can&rsquo;t self-delete. Remove your email from
                <code className="text-xs bg-muted px-1 rounded mx-1">ADMIN_EMAILS</code>
                first or ask another admin.
              </p>
            ) : isOwner ? (
              <p className="text-sm text-muted-foreground mt-1">
                Permanently removes your account. Schedules for any
                <strong className="text-foreground"> unpublished </strong>
                trucks are deleted. Trucks with a <strong className="text-foreground">published profile</strong>
                {' '}will be transferred to admin for review (so customers
                following them aren&rsquo;t left with broken links).
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                Permanently removes your account, follows, and any reports
                you&rsquo;ve sent us. Aggregate analytics counts are kept but
                no longer linked to you. This cannot be undone.
              </p>
            )}

            {!isAdmin && !confirmOpen && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmOpen(true)}
                className="mt-4 gap-2 border-rose-500/40 text-rose-700 hover:bg-rose-500/10 hover:text-rose-800"
                data-testid="settings-delete-open"
              >
                <Trash weight="duotone" className="h-4 w-4" />
                Delete my account
              </Button>
            )}

            {confirmOpen && (
              <form onSubmit={onDelete} className="mt-4 space-y-3" data-testid="settings-delete-form">
                <div className="space-y-1.5">
                  <Label htmlFor="settings-password" className="text-xs uppercase tracking-wider">
                    Password
                  </Label>
                  <Input
                    id="settings-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="settings-delete-password"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Signed up with Apple or Google? Leave this blank.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="settings-confirm" className="text-xs uppercase tracking-wider">
                    Type <span className="font-mono text-rose-600">DELETE</span> to confirm
                  </Label>
                  <Input
                    id="settings-confirm"
                    type="text"
                    autoComplete="off"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE"
                    required
                    data-testid="settings-delete-confirm"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    type="submit"
                    disabled={loading || confirmText !== "DELETE" || !password}
                    className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
                    data-testid="settings-delete-submit"
                  >
                    <Trash weight="duotone" className="h-4 w-4" />
                    {loading ? "Deleting…" : "Permanently delete"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setConfirmOpen(false);
                      setPassword("");
                      setConfirmText("");
                    }}
                    disabled={loading}
                    data-testid="settings-delete-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
        <ShieldCheck weight="duotone" className="h-3.5 w-3.5 text-emerald-600" />
        Your data lives on TruckTrackr&rsquo;s own infrastructure. No third-party trackers.
      </p>
      <p className="text-[10px] text-muted-foreground/70" data-testid="settings-build-stamp">
        TruckTrackr v{APP_VERSION} · {BUILD_STAMP}
      </p>
    </div>
  );
}
